import { Checkbox } from '../../../../../../widgets/checkbox';
import { Input } from '../../../../../../widgets/input';
import { MessageDialog, useDialogManager } from '../../../../../../widgets/dialog';
import { showErrorNotification } from '../../../../../../widgets/notificationToaster';
import { debounce } from 'lodash';
import React, { FC } from 'react';
import { useProductConfigurationState } from 'src/components/pages/productConfiguration/state';
import { T } from 'src/shared/helpers';
import Copy from 'src/shared/material_icons/content/content_copy.svg';
import Edit from 'src/shared/material_icons/image/edit.svg';
import Retarget from 'src/shared/material_icons/action/open_in_full.svg';
import Link from 'src/shared/material_icons/content/link.svg';
import Plus from 'src/shared/material_icons/content/add.svg';
import Trash from 'src/shared/material_icons/action/delete.svg';
import theme from 'src/shared/theme';
import styled from 'styled-components';
import { ComposerOptionTreeElement } from '../../../configurator-composer/composer/components/composerOptionTreeElement';
import useComposerStore from '../../../configurator-composer/composer/composerStore';
import ComposerLinkAttributeDialog from '../../../configurator-composer/composer/dialogs/composerLinkAttributeDialog';
import ComposerRetargetAttributeDialog from '../../../configurator-composer/composer/dialogs/composerRetargetAttributeDialog';
import ComposerSaveOptionDialog from '../../../configurator-composer/composer/dialogs/save/composerSaveOptionDialog';
import { getRetargetObjectsAndMaterials, retargetMapToActions, sortByDisplay } from '../../../configurator-composer/composer/helpers/composerHelpers';
import { TreeNodeHook } from '../../scene-editors/hooks/useTreeNodeManager';
import { ToolGrid } from '../../toolsgrid';
import { ComposerAttributeNode, ComposerAttributeOptionNode } from './composerAttributesModels';

const ComposerOptionsContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 25%;
    height: 100%;
    padding: 0 15px;
    padding-top: 24px;
    border-right: 1px solid ${theme.app.borderColor};
`;

const ComposerOptionsHeader = styled.div`
    display: flex;
    width: 100%;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 10px;
    border-bottom: 1px solid ${theme.app.borderColor};
    min-height: 160px;
`;

const ComposerOptionsSearchContainer = styled.div`
    width: 100%;
    & > input {
        width: 100%;
    }
`;
const ComposerOptionsBody = styled.div`
    padding: 15px 0;
    flex: 1;
    overflow-y: auto;
    position: relative;
`;
const ComposerOptionsList = styled.div<{ $hasOptions: boolean }>`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 85%;
    gap: 10px;
    padding: 25px 0;
    overflow-y: auto;

    ${(props) => !props.$hasOptions && 'justify-content: center; align-items: center;'}
`;

const ComposerOptionsPreview = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    justify-content: center;
    align-items: center;
`;

const ComposerAttributeDeselectorContainer = styled.div`
    position: absolute;
    height: 30px;
    top: 10px;
    left: 0;
    z-index: 10;
    cursor: pointer;
`;
const ComposerOptionsSelector: FC<{ selected: boolean; onClick: () => void }> = ({ selected, onClick }) => {
    return (
        <ComposerAttributeDeselectorContainer onClick={onClick}>
            <Checkbox checked={selected} onChange={onClick} label='Select all items' />
        </ComposerAttributeDeselectorContainer>
    );
};

const ComposerOptions: FC<{
    selectedAttribute: ComposerAttributeNode | null;
    options: ComposerAttributeOptionNode[];
    multiple: boolean;
    state: TreeNodeHook<ComposerAttributeOptionNode>;
}> = ({ selectedAttribute, options, multiple, state }) => {
    // HOOKS FIRST - non possono essere condizionali!
    const [
        attributes,
        sceneMaterialList,
        sceneMeshList,
        updateOption,
        removeOption,
        cloneOption,
        selectOptions,
        selectAttributes,
        validateOptions,
        selectedOptions,
        storedEcommerceSettings
    ] = useComposerStore((state) => [
        state.attributes,
        state.sceneMaterialList,
        state.sceneObjectList,
        state.updateOption,
        state.removeOption,
        state.cloneOption,
        state.selectOptions,
        state.selectAttributes,
        state.validateOptions,
        state.selectedOptions,
        state.ecommerceSettings
    ]);
    const { showDialog, closeDialog } = useDialogManager();
    const { state: productState } = useProductConfigurationState();
    const { filter, setFilter, selectedIds, filteredData, addNode, findNodeById, updateNode, removeNodes, setSelectedIds } = state;

    React.useEffect(() => {
        if (selectedOptions && selectedOptions.length > 0) {
            const optionIds = selectedOptions.map((opt) => opt.id);
            const currentIds = [...selectedIds].sort().join(',');
            const storeIds = [...optionIds].sort().join(',');

            if (currentIds !== storeIds) {
                console.log('Syncing selectedIds with store | store has:', optionIds.length, '| local has:', selectedIds.length);
                // Use queueMicrotask to ensure this happens after tree reinit
                queueMicrotask(() => {
                    setSelectedIds(optionIds);
                });
            }
        } else if (selectedIds.length > 0 && (!selectedOptions || selectedOptions.length === 0)) {
            // Store has no selection, clear local selection
            console.log('Clearing local selectedIds (store is empty)');
            setSelectedIds([]);
        }
    }, [selectedOptions, options, selectedIds]); // Re-sync when options change (tree reinit)

    // Early return AFTER all hooks - se no attribute è selezionato
    if (!selectedAttribute) {
        return (
            <ComposerOptionsContainer>
                <ComposerOptionsHeader>
                    <>{T._('Select an attribute to manage options', 'Admin')}</>
                </ComposerOptionsHeader>
            </ComposerOptionsContainer>
        );
    }

    const saveOption = (passedOption: ComposerAttributeOptionNode | null) => {
        // Guard: verifica che selectedAttribute esista PRIMA di creare il JSX
        if (!selectedAttribute) {
            console.error('❌ Cannot save option: selectedAttribute is null or undefined');
            showErrorNotification({
                message: T._('Please select an attribute first', 'Admin')
            });
            return;
        }

        showDialog(
            'saveOption',

            <ComposerSaveOptionDialog
                onConfirm={(option: ComposerAttributeOptionNode | null) => {
                    if (!option || !selectedAttribute) return;
                    option.attributeID = selectedAttribute.id;
                    option.isNew = true;
                    if (passedOption) {
                        updateNode(passedOption.id, { ...passedOption, ...option });
                    } else {
                        addNode(option);
                    }
                    updateOption(option);
                    closeDialog('saveOption');
                }}
                productCode={productState.product.code}
                salesChannelID={productState.salesChannelID}
                edit={!!passedOption}
                attributeID={selectedAttribute.id}
                passedOption={passedOption}
                selectedPlatformAttribute={selectedAttribute.platformAttribute}
            />
        );
    };
    const onOptionsHeaderButtonClick = (action: string) => {
        switch (action) {
            case 'add':
                saveOption(null);
                break;
            case 'edit':
                if (selectedIds.length !== 1) return;
                const selectedOption = findNodeById(selectedIds[0]);
                saveOption(selectedOption!);
                break;
            case 'clone':
                let newIDsToSelect: string[] = [];
                selectedIds.forEach((id) => {
                    const currentNode = findNodeById(id);
                    if (currentNode) {
                        const newNode = cloneOption(currentNode);
                        addNode(newNode);
                        updateOption(newNode);
                        newIDsToSelect.push(newNode.id);
                    }
                });
                setSelectedIds(newIDsToSelect);
                break;
            case 'remove':
                // Get nodes BEFORE removing them from tree state
                const nodesToRemove = selectedIds.map((id) => findNodeById(id)).filter((node) => node !== null);

                if (nodesToRemove.length === 0) {
                    console.warn('No options selected for removal');
                    return;
                }

                showDialog(
                    'confirmDialog',
                    <MessageDialog
                        title={T._('Confirm Removal', 'Admin')}
                        message={T._('Are you sure you want to remove the selected option(s)? ', 'Admin') + nodesToRemove.map((node) => node?.name).join(', ')}
                        buttons={[
                            { label: T._('Cancel', 'Admin'), outline: true, onClick: () => closeDialog('confirmDialog') },
                            {
                                label: T._('Remove', 'Admin'),
                                destructive: true,
                                onClick: () => {
                                    closeDialog('confirmDialog');

                                    // Remove from local tree state
                                    removeNodes(selectedIds);
                                    setSelectedIds([]);

                                    // Remove from global store
                                    nodesToRemove.forEach((node) => {
                                        if (node) {
                                            removeOption(node.attributeID, node.id);
                                        }
                                    });
                                }
                            }
                        ]}
                    />
                );
                break;
            case 'link':
                if (selectedIds.length !== 1) return;
                const selectedNode = findNodeById(selectedIds[0]);
                if (!selectedNode) return;
                showDialog(
                    'linkAttributes',
                    <ComposerLinkAttributeDialog
                        currentAttribute={selectedAttribute}
                        modelAttributes={attributes || []}
                        currentOption={selectedNode}
                        onConfirm={(result) => {
                            closeDialog('linkAttributes');
                            if (!result || !result.links) return;
                            const filteredLinks = result.links
                                .filter((l) => l.attributeID && l.optionID)
                                .map((l) => ({
                                    attributeID: l.attributeID,
                                    operation: l.comparisonOperator,
                                    optionID: l.optionID
                                }));
                            const updatedOption: ComposerAttributeOptionNode = {
                                ...selectedNode,
                                linkOperation: result.logicalOperator,
                                links: filteredLinks
                            };
                            updateOption(updatedOption);
                            updateNode(selectedNode.id, updatedOption);
                        }}
                    />
                );
                break;
            case 'retarget':
                if (!selectedOptions || selectedOptions?.length === 0) {
                    console.warn('Selected attribute has no options to retarget');
                    return;
                }
                const actions = selectedOptions.flatMap((opt) => opt.actions || []);
                const { objectIds, materialIds } = getRetargetObjectsAndMaterials(actions);
                showDialog(
                    'composerRetargetAttribute',
                    <ComposerRetargetAttributeDialog
                        materialsList={sceneMaterialList}
                        objectsList={sceneMeshList}
                        usedMaterialsList={sceneMaterialList.filter((m) => materialIds.includes(m.id))}
                        usedObjectsList={sceneMeshList.filter((o) => objectIds.includes(o.name || ''))}
                        onConfirm={(data) => {
                            if (!data) {
                                closeDialog('composerRetargetAttribute');
                                return;
                            }
                            closeDialog('composerRetargetAttribute');
                            const { materialMap, objectMap } = data;
                            retargetMapToActions(actions, materialMap, objectMap);
                            selectedOptions.forEach((option) => {
                                option.actions = actions.filter((a) => option.actions?.some((oa) => oa.id === a.id));
                                updateOption(option);
                                updateNode(option.id, option);
                            });
                        }}
                    />
                );
                break;
            default:
                console.warn(`Unknown action: ${action}`);
        }
    };

    //add,edit,delete,link,image
    const buttons = [
        { icon: <Plus />, name: 'add', tooltip: T._('Add Attribute', 'Admin'), onClick: () => onOptionsHeaderButtonClick('add') },
        {
            icon: <Edit />,
            name: 'edit',
            tooltip: T._('Edit Attribute', 'Admin'),
            onClick: () => onOptionsHeaderButtonClick('edit'),

            disabled: selectedIds.length !== 1
        },
        {
            icon: <Copy />,
            name: 'clone',
            tooltip: T._('Clone Attribute', 'Admin'),
            onClick: () => onOptionsHeaderButtonClick('clone'),
            disabled: selectedIds.length === 0
        },
        {
            icon: <Trash />,
            name: 'remove',
            tooltip: T._('Remove Attribute', 'Admin'),
            onClick: () => onOptionsHeaderButtonClick('remove'),
            disabled: selectedIds.length === 0
        },
        {
            icon: <Link />,
            name: 'link',
            tooltip: T._('Link Attribute', 'Admin'),
            onClick: () => onOptionsHeaderButtonClick('link'),

            disabled: selectedIds.length !== 1
        },
        {
            icon: <Retarget />,
            name: 'retarget',
            tooltip: T._('Retarget Attribute', 'Admin'),
            onClick: () => onOptionsHeaderButtonClick('retarget'),
            disabled: selectedIds.length !== 1
        }
    ];

    const searchOptions = (searchTerm: string) => {
        const debouncedSearch = debounce((term) => {
            setFilter(term);
        }, 300);

        debouncedSearch(searchTerm);
    };

    const handleSetDefaultOption = (option: ComposerAttributeOptionNode) => {
        // Guard: verifica che selectedAttribute esista
        if (!selectedAttribute) {
            console.error('❌ Cannot set default option: selectedAttribute is null or undefined');
            return;
        }

        const currentAttribute = useComposerStore.getState().attributes.find((attr) => attr.id === selectedAttribute.id);
        if (!currentAttribute) return;

        // Preserve current selection
        const currentlySelectedIds = [...selectedIds];

        // Update store with new isDefault values
        useComposerStore.setState((state) => {
            const attributeIndex = state.attributes.findIndex((attr) => attr.id === selectedAttribute.id);
            if (attributeIndex === -1) return state;

            const updatedAttribute = {
                ...state.attributes[attributeIndex],
                options: state.attributes[attributeIndex].options.map((opt) => ({
                    ...opt,
                    isDefault: opt.id === option.id
                }))
            };

            const newState = {
                ...state,
                attributes: [...state.attributes.slice(0, attributeIndex), updatedAttribute, ...state.attributes.slice(attributeIndex + 1)],
                selectedAttributes:
                    state.selectedAttributes?.map((attr) => (attr.id === updatedAttribute.id ? updatedAttribute : attr)) ?? state.selectedAttributes
            };

            return newState;
        });

        // The tree will reinitialize when options change, but the useEffect will restore selection
    };

    const hasOptions = options.length > 0;
    const hasFilteredOptions = filteredData.length > 0;

    const nodeSelectHandler = (e: React.MouseEvent, node: ComposerAttributeOptionNode) => {
        const ctrlKey = e.ctrlKey || e.metaKey;
        const shiftKey = e.shiftKey;

        // Se c'è il tasto shift, seleziono un intervallo di nodi
        if (shiftKey) {
            const lastSelectedId = selectedIds[selectedIds.length - 1];
            const lastSelectedNode = findNodeById(lastSelectedId);
            if (!lastSelectedNode) return;

            const startIndex = filteredData.findIndex((n) => n.id === lastSelectedId);
            const endIndex = filteredData.findIndex((n) => n.id === node.id);

            if (startIndex === -1 || endIndex === -1) return;

            const range = filteredData.slice(Math.min(startIndex, endIndex), Math.max(startIndex, endIndex) + 1);
            selectOptions(range);
            setSelectedIds(range.map((n) => n.id));
            return;
        }

        // con control key, aggiungo o rimuovo l'elemento selezionato
        if (ctrlKey && selectedIds.includes(node.id)) {
            setSelectedIds(selectedIds.filter((id) => id !== node.id));
            selectOptions(filteredData.filter((n) => n.id !== node.id));
            return;
        } else if (ctrlKey) {
            selectOptions([...filteredData, node]);
            setSelectedIds((prev) => [...prev, node.id]);
            return;
        }
        // se non c'è control key, seleziono solo l'elemento corrente
        selectOptions([node]);
        setSelectedIds([node.id]);
    };

    const moveNodeHandler = (node: ComposerAttributeOptionNode, direction: 'up' | 'down') => {
        // Guard: verifica che selectedAttribute esista
        if (!selectedAttribute) {
            console.error('❌ Cannot move option: selectedAttribute is null or undefined');
            return;
        }

        // Use getState() to read the LATEST data from store, avoiding stale closures
        const currentAttribute = useComposerStore.getState().attributes.find((attr) => attr.id === selectedAttribute.id);
        const currentOptions = currentAttribute?.options ?? [];
        const siblings = currentOptions.sort((a, b) => sortByDisplay(currentOptions, a, b));

        const currentIndex = siblings.findIndex((n) => n.id === node.id);
        const swapIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
        if (swapIndex >= 0 && swapIndex < siblings.length) {
            const swapNode = siblings[swapIndex];
            const currentOrder = node.displayOrder;

            updateNode(node.id, { ...node, displayOrder: swapNode.displayOrder });
            updateNode(swapNode.id, { ...swapNode, displayOrder: currentOrder });
            updateOption({ ...node, displayOrder: swapNode.displayOrder });
            updateOption({ ...swapNode, displayOrder: currentOrder });
        }
    };

    const dragStartHandler = (e: React.DragEvent<HTMLDivElement>, node: ComposerAttributeOptionNode) => {
        e.dataTransfer.setData('text/plain', node.id);
        e.dataTransfer.effectAllowed = 'move';
    };

    const dragOverHandler = (e: React.DragEvent<HTMLDivElement>, node: ComposerAttributeOptionNode) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const dropHandler = (e: React.DragEvent<HTMLDivElement>, dropTargetNode: ComposerAttributeOptionNode | null) => {
        e.preventDefault();
        const draggedNodeId = e.dataTransfer.getData('text/plain');
        const draggedNode = findNodeById(draggedNodeId);

        if (!draggedNode) return;

        if (!dropTargetNode) return;

        // Guard: verifica che selectedAttribute esista
        if (!selectedAttribute) {
            console.error('❌ Cannot drop option: selectedAttribute is null or undefined');
            return;
        }

        // Use getState() to read the LATEST data from store, avoiding stale closures
        const currentAttribute = useComposerStore.getState().attributes.find((attr) => attr.id === selectedAttribute.id);
        const currentOptions = currentAttribute?.options ?? [];
        const siblings = currentOptions.sort((a, b) => sortByDisplay(currentOptions, a, b));

        const dropIndex = siblings.findIndex((n) => n.id === dropTargetNode.id);

        // Update dragged node
        updateNode(draggedNode.id, { ...draggedNode, displayOrder: dropIndex });
        updateOption({ ...draggedNode, displayOrder: dropIndex });

        // Update siblings in new parent
        for (let i = dropIndex; i < siblings.length; i++) {
            const sibling = siblings[i];
            updateNode(sibling.id, { ...sibling, displayOrder: i + 1 });
            updateOption({ ...sibling, displayOrder: i + 1 });
        }
    };

    if (multiple) {
        return (
            <ComposerOptionsContainer>
                <div>{T._('No options available.', 'Admin')}</div>
            </ComposerOptionsContainer>
        );
    }
    const nodes = React.useMemo(() => filteredData.sort((a, b) => sortByDisplay(filteredData, a, b)), [filteredData]);

    // Auto-select first option when attribute changes
    React.useEffect(() => {
        // Guard: verifica che selectedAttribute esista
        if (!selectedAttribute) {
            return;
        }

        // Wait for tree state to be ready, then select first option
        const timer = setTimeout(() => {
            if (options.length > 0) {
                const sortedOptions = [...options].sort((a, b) => a.displayOrder - b.displayOrder);
                const firstOption = sortedOptions[0];
                if (!firstOption) return;
                // Force selection even if there's already something selected
                setSelectedIds([firstOption.id]);
                selectOptions([firstOption]);
            } else {
                setSelectedIds([]);
                selectOptions([]);
            }
        }, 100); // Increased delay to ensure tree state is fully ready

        return () => clearTimeout(timer);
    }, [selectedAttribute?.id]); // Only trigger when attribute changes, not when options are updated

    return (
        <ComposerOptionsContainer>
            <ComposerOptionsHeader>
                <ToolGrid tools={buttons} columns={buttons.length} />
                <ComposerOptionsSearchContainer>
                    <Input
                        type='text'
                        placeholder={T._('Search Options....', 'Admin')}
                        disabled={!hasOptions}
                        onChange={(e) => searchOptions(e.target.value)}
                    />
                </ComposerOptionsSearchContainer>
            </ComposerOptionsHeader>
            <ComposerOptionsBody>
                <ComposerOptionsList $hasOptions={hasFilteredOptions}>
                    {hasOptions && (
                        <ComposerOptionsSelector
                            selected={selectedIds.length === filteredData.length && filteredData.length > 0}
                            onClick={() => {
                                if (selectedIds.length === filteredData.length) {
                                    // Deselect all
                                    setSelectedIds([]);
                                    selectOptions([]);
                                } else {
                                    // Select all options
                                    const allOptions = filteredData.map((n) => n.id);
                                    setSelectedIds(allOptions);
                                    // Update store with all selected options
                                    selectOptions(filteredData);
                                }
                            }}
                        />
                    )}
                    {!hasOptions ? (
                        <div>{T._('No options found, create a new one.', 'Admin')}</div>
                    ) : (
                        hasOptions && !hasFilteredOptions && <div>{T._('No options match your search.', 'Admin')}</div>
                    )}{' '}
                    {nodes.map((option) => (
                        <ComposerOptionTreeElement
                            key={option.id}
                            option={option}
                            selected={selectedIds.includes(option.id)}
                            onSelect={nodeSelectHandler}
                            hasLink={option.links?.length > 0}
                            onDoubleClick={(_, option) => {
                                saveOption(option);
                            }}
                            onSetDefault={handleSetDefaultOption}
                            onMoveDown={() => moveNodeHandler(option, 'down')}
                            onMoveUp={() => moveNodeHandler(option, 'up')}
                            onDragOver={(e) => dragOverHandler(e, option)}
                            onDragStart={(e) => dragStartHandler(e, option)}
                            onDrop={(e) => dropHandler(e, option)}
                        />
                    ))}
                </ComposerOptionsList>
            </ComposerOptionsBody>
        </ComposerOptionsContainer>
    );
};

export default ComposerOptions;
